import React, { useState } from 'react';
import { FaApple } from 'react-icons/fa';
import { FiLoader } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { isApplePlatform } from '../../services/platformService';

function AppleSignInButton({ mode = 'signin', onError, onSuccess }) {
  const { signInWithApple } = useAuth();
  const [loading, setLoading] = useState(false);

  // Hide outside Apple platforms - Android Chrome / Windows users have no
  // realistic path through Sign in with Apple, so showing the button just
  // adds noise. The Login / SignUp pages also hide the surrounding "or"
  // divider via the same gate, but we early-return here as belt-and-braces
  // so any new caller automatically gets the right behaviour.
  if (!isApplePlatform()) return null;

  const label = mode === 'signup' ? 'Sign up with Apple' : 'Continue with Apple';

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data, error } = await signInWithApple();
      if (error) {
        const raw = error?.message || '';
        const cancelled = /cancel/i.test(raw) || error?.code === 'ERR_CANCELED';
        if (!cancelled) onError?.(raw || 'Apple Sign In failed');
        // Auth failed/cancelled - re-enable the button.
        setLoading(false);
        return;
      }
      // Success: keep the spinner running. Auth state propagation + the
      // post-sign-in redirect (login → home/onboarding) will tear this
      // component down. If we cleared loading here, the button would briefly
      // look idle during the handoff and the tap could feel like it did
      // nothing.
      onSuccess?.(data);
    } catch (err) {
      // Defensive: the AuthContext wrapper already converts throws into
      // {error}, but in case anything slips past we still want the button
      // back in idle state.
      setLoading(false);
      throw err;
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      aria-label={label}
      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro", system-ui, sans-serif' }}
    >
      {loading ? (
        <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
      ) : (
        <>
          <SafeIcon icon={FaApple} className="w-5 h-5 -mt-0.5" />
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

export default AppleSignInButton;
